import React from 'react';
import { useLang } from '../context/LanguageContext';
import { ScrollText, RefreshCcw, Shield, AlertTriangle, Mail, Phone } from 'lucide-react';

const content = {
  en: {
    kicker: '— Legal —',
    title: 'Terms of Service',
    updated: 'Last updated: February 06, 2026',
    intro: 'These Terms of Service ("Terms") govern your access to and use of the iAccounting website, portal, and all consultation, company-formation, and advisory services provided by iAccounting Company Consultation (the "Company", "we", "our", or "us") in the United Arab Emirates. By using our services you agree to these Terms.',
    sections: [
      {
        icon: ScrollText,
        title: '1. Services',
        body: [
          'iAccounting provides business setup advisory, freezone and mainland company formation, corporate documentation, VAT registration, and related administrative services within the UAE.',
          'All engagements are subject to a written scope confirmation via our portal, quotation, or a signed service agreement.',
          'Timelines quoted are indicative and depend on the respective government authorities (DED, MOHRE, freezone regulators) whose delays are outside our control.'
        ]
      },
      {
        icon: Shield,
        title: '2. Client Obligations',
        body: [
          'You warrant that all information and documents you submit (passport copies, Emirates ID, NOCs, activity choices, share capital details) are true, complete, and lawful.',
          'You must respond to requests for signatures, in-person visits, or additional documentation within a reasonable time. Failure to do so may delay the application without any liability on us.',
          'You are responsible for all government fees, third-party charges, translation and attestation costs — separately from our service fee, unless bundled explicitly in your package.'
        ]
      },
      {
        icon: RefreshCcw,
        title: '3. Cancellation & Refund Policy',
        body: [
          'You may cancel your engagement at any time by written notice to info@iaccounting.ae. Refunds are governed strictly by the stage the file has reached on the cancellation date:',
        ],
        table: [
          { stage: 'Before any work commenced (no consultation done, no submissions)', refund: '100% refund of amounts paid, less any payment-gateway fees.' },
          { stage: 'After initial consultation and document collection, before any government submission', refund: '70% refund of the professional service fee. Consultation fee (if any) is non-refundable.' },
          { stage: 'After name reservation, initial approval, or any government submission on your behalf', refund: '30% refund of the professional service fee. Government fees already paid are non-refundable.' },
          { stage: 'After license or trade name issuance', refund: 'Non-refundable. Amendments may be offered as a courtesy at our discretion.' },
          { stage: 'Cancellation due to rejection by the authority for reasons not attributable to us (e.g., activity not permitted, ineligible shareholder)', refund: 'Refund of unused government fees only, where the authority itself refunds them. Service fee is non-refundable.' },
          { stage: 'Cancellation due to a proven failure on our side (missed deadline, incorrect filing our fault)', refund: '100% refund of the professional service fee, plus reimbursement of any wasted government fees where recoverable.' }
        ],
        after: [
          'Approved refunds are processed within 14 business days to the original payment method. Bank transfer refunds may take additional time depending on the beneficiary bank.',
          'Refunds are issued in AED. Foreign-currency conversion differences and payment-gateway charges are not reimbursed.',
          'To request a refund send: (a) your Application ref (APP-YYYY-XXXX), (b) reason for cancellation, and (c) preferred refund method to info@iaccounting.ae.'
        ]
      },
      {
        icon: AlertTriangle,
        title: '4. Limitation of Liability',
        body: [
          'Our maximum aggregate liability for any claim arising out of the services is limited to the professional service fee actually received by us for the specific engagement.',
          'We are not liable for indirect, incidental, consequential or loss-of-profit damages, or for delays caused by government authorities, banks, courier services, or force majeure events.'
        ]
      },
      {
        icon: Shield,
        title: '5. Confidentiality & Data Protection',
        body: [
          'We handle your personal data and corporate information in line with UAE Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data.',
          'Your documents are used solely for the purpose of processing your application and are not shared with third parties except the relevant government authorities, licensed courier partners, and, where applicable, notaries.'
        ]
      },
      {
        icon: ScrollText,
        title: '6. Governing Law & Disputes',
        body: [
          'These Terms are governed by the laws of the United Arab Emirates and the applicable laws of the Emirate of Dubai.',
          'Any dispute arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Dubai, UAE.'
        ]
      }
    ],
    contactTitle: 'Questions?',
    contactBody: 'For any question about these Terms or to request a cancellation/refund, contact us:',
    email: 'info@iaccounting.ae',
    phone: '+971 55 109 6418',
    address: 'Business Bay, Dubai, UAE',
    tableHeaders: { stage: 'Cancellation stage', refund: 'Refund entitlement' }
  },
  ar: {
    kicker: '— قانوني —',
    title: 'شروط الخدمة',
    updated: 'آخر تحديث: 06 فبراير 2026',
    intro: 'تحكم شروط الخدمة هذه ("الشروط") وصولك إلى موقع iAccounting والبوابة الإلكترونية واستخدامك لجميع خدمات الاستشارات وتأسيس الشركات والاستشارات ذات الصلة المقدَّمة من قِبَل iAccounting Company Consultation ("الشركة" أو "نحن") في دولة الإمارات العربية المتحدة. باستخدامك خدماتنا فإنك توافق على هذه الشروط.',
    sections: [
      {
        icon: ScrollText,
        title: '١. الخدمات',
        body: [
          'تقدّم iAccounting استشارات تأسيس الأعمال، وتأسيس الشركات في المناطق الحرة والبر الرئيسي، وإعداد المستندات المؤسسية، وتسجيل ضريبة القيمة المضافة، والخدمات الإدارية ذات الصلة داخل الإمارات.',
          'تخضع كل مهمة لتأكيد نطاق مكتوب عبر البوابة أو عرض السعر أو اتفاقية خدمة موقّعة.',
          'المواعيد المذكورة إرشادية وتعتمد على الجهات الحكومية المعنية (DED وMOHRE ومنظّمو المناطق الحرة) والتأخير الحاصل من قِبَلها خارج سيطرتنا.'
        ]
      },
      {
        icon: Shield,
        title: '٢. التزامات العميل',
        body: [
          'تُقرّ بأن جميع المعلومات والمستندات التي تقدّمها (نسخ جواز السفر، الهوية الإماراتية، رسائل عدم الممانعة، النشاط، رأس المال) صحيحة وكاملة وقانونية.',
          'يجب الاستجابة لطلبات التوقيع أو الحضور الشخصي أو تقديم مستندات إضافية خلال وقت معقول. أي تأخير من طرفك قد يؤخّر الطلب دون أي مسؤولية علينا.',
          'أنت مسؤول عن جميع الرسوم الحكومية ورسوم الأطراف الثالثة والترجمة والتصديق، بمعزل عن رسوم خدمتنا، ما لم تُذكر ضمن باقتك صراحةً.'
        ]
      },
      {
        icon: RefreshCcw,
        title: '٣. سياسة الإلغاء واسترداد الأموال',
        body: [
          'يمكنك إلغاء التعاقد في أي وقت بإشعار مكتوب إلى info@iaccounting.ae. تحدَّد استعادة الأموال بشكل صارم وفقًا للمرحلة التي وصل إليها الملف في تاريخ الإلغاء:'
        ],
        table: [
          { stage: 'قبل بدء أي عمل (لا استشارة ولا تقديمات)', refund: 'استرداد ١٠٠٪ من المبالغ المدفوعة، مع خصم رسوم بوابة الدفع إن وُجدت.' },
          { stage: 'بعد الاستشارة الأولية وجمع المستندات، قبل أي تقديم حكومي', refund: 'استرداد ٧٠٪ من رسوم الخدمة المهنية. رسوم الاستشارة (إن وُجدت) غير قابلة للاسترداد.' },
          { stage: 'بعد حجز الاسم أو الموافقة المبدئية أو أي تقديم حكومي بالنيابة عنك', refund: 'استرداد ٣٠٪ من رسوم الخدمة المهنية. الرسوم الحكومية المدفوعة غير قابلة للاسترداد.' },
          { stage: 'بعد إصدار الرخصة أو الاسم التجاري', refund: 'غير قابل للاسترداد. قد نتفضل بتعديلات مجاملةً وفق تقديرنا.' },
          { stage: 'الإلغاء بسبب رفض من الجهة لأسباب لا تُعزى إلينا (نشاط غير مسموح، مساهم غير مؤهل...)', refund: 'يُستردّ فقط ما تُعيده الجهة الحكومية من رسومها. رسوم خدمتنا غير قابلة للاسترداد.' },
          { stage: 'الإلغاء بسبب تقصير مثبت من طرفنا (تأخير أو تقديم خاطئ بسببنا)', refund: 'استرداد ١٠٠٪ من رسوم الخدمة المهنية، مع تعويض الرسوم الحكومية الضائعة حيث يمكن استردادها.' }
        ],
        after: [
          'تتم معالجة استرداد الأموال الموافق عليها خلال ١٤ يوم عمل إلى وسيلة الدفع الأصلية. قد تستغرق الحوالات البنكية وقتًا إضافيًا حسب البنك المستفيد.',
          'يتم الاسترداد بالدرهم الإماراتي. لا نعوّض فروقات صرف العملات ولا رسوم بوابة الدفع.',
          'لطلب الاسترداد أرسل: (أ) رقم الطلب (APP-YYYY-XXXX)، (ب) سبب الإلغاء، (ج) طريقة الاسترداد المفضّلة إلى info@iaccounting.ae.'
        ]
      },
      {
        icon: AlertTriangle,
        title: '٤. حدود المسؤولية',
        body: [
          'تقتصر مسؤوليتنا القصوى الإجمالية عن أي مطالبة ناشئة عن الخدمات على قيمة رسوم الخدمة المهنية التي استلمناها فعليًا للتعاقد المعني.',
          'لا نتحمّل مسؤولية الأضرار غير المباشرة أو التبعية أو فوات الربح أو أي تأخير يعود إلى الجهات الحكومية أو البنوك أو شركات الشحن أو حالات القوة القاهرة.'
        ]
      },
      {
        icon: Shield,
        title: '٥. السرية وحماية البيانات',
        body: [
          'نتعامل مع بياناتك الشخصية ومعلومات شركتك وفق المرسوم الاتحادي الإماراتي رقم ٤٥ لسنة ٢٠٢١ بشأن حماية البيانات الشخصية.',
          'تُستخدم مستنداتك فقط لأغراض إتمام طلبك ولا تُشارَك مع أي طرف ثالث سوى الجهات الحكومية المعنية وشركات الشحن المرخّصة وكتّاب العدل عند الحاجة.'
        ]
      },
      {
        icon: ScrollText,
        title: '٦. القانون الحاكم والنزاعات',
        body: [
          'تخضع هذه الشروط لقوانين دولة الإمارات العربية المتحدة والقوانين السارية في إمارة دبي.',
          'يخضع أي نزاع ناشئ عن هذه الشروط للاختصاص الحصري لمحاكم دبي، الإمارات العربية المتحدة.'
        ]
      }
    ],
    contactTitle: 'أسئلة؟',
    contactBody: 'لأي استفسار حول هذه الشروط أو لطلب إلغاء / استرداد، تواصل معنا:',
    email: 'info@iaccounting.ae',
    phone: '+971 55 109 6418',
    address: 'الخليج التجاري، دبي، الإمارات',
    tableHeaders: { stage: 'مرحلة الإلغاء', refund: 'الاسترداد المستحق' }
  }
};

const Terms = () => {
  const { lang, dir } = useLang();
  const c = content[lang] || content.en;

  return (
    <div className="pt-28 pb-20 min-h-screen" dir={dir} data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-[#2563eb] mb-4">{c.kicker}</div>
          <h1 className="font-display text-5xl md:text-6xl text-[#0a0a0a] leading-tight">{c.title}</h1>
          <div className="mt-4 text-xs text-[#111827]/40">{c.updated}</div>
        </div>

        {/* Intro */}
        <div className="glass-card rounded-2xl p-6 md:p-8 mb-10 text-[#111827]/80 text-base leading-relaxed">
          {c.intro}
        </div>

        {/* Sections */}
        <div className="space-y-10">
          {c.sections.map((sec, i) => {
            const Icon = sec.icon;
            return (
              <section key={i} data-testid={`terms-section-${i + 1}`} className="scroll-mt-32">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#2563eb]/10 border border-[#2563eb]/25 flex items-center justify-center">
                    <Icon className="w-4.5 h-4.5 text-[#3b82f6]" strokeWidth={1.75} />
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl text-[#0a0a0a]">{sec.title}</h2>
                </div>
                <div className="glass-card rounded-2xl p-6 md:p-8 space-y-4">
                  {sec.body.map((p, j) => (
                    <p key={j} className="text-[#111827]/75 text-sm md:text-base leading-relaxed">{p}</p>
                  ))}
                  {sec.table && (
                    <div className="overflow-x-auto pt-2" data-testid="refund-table">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="border-b-2 border-[#2563eb]/40">
                            <th className="text-start py-3 pe-3 text-[10px] uppercase tracking-widest text-[#2563eb] font-semibold w-1/2">{c.tableHeaders.stage}</th>
                            <th className="text-start py-3 ps-3 text-[10px] uppercase tracking-widest text-[#2563eb] font-semibold">{c.tableHeaders.refund}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sec.table.map((row, k) => (
                            <tr key={k} className="border-b border-[#2563eb]/10 align-top">
                              <td className="py-3 pe-3 text-[#111827]/80 text-sm">{row.stage}</td>
                              <td className="py-3 ps-3 text-[#3b82f6] text-sm font-medium">{row.refund}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {sec.after && sec.after.map((p, k) => (
                    <p key={`a-${k}`} className="text-[#111827]/75 text-sm md:text-base leading-relaxed">{p}</p>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* Contact */}
        <div className="mt-14 glass-card rounded-2xl p-8 md:p-10 border border-[#2563eb]/25" data-testid="terms-contact">
          <div className="font-display text-2xl md:text-3xl text-[#0a0a0a] mb-3">{c.contactTitle}</div>
          <p className="text-[#111827]/70 text-sm mb-5">{c.contactBody}</p>
          <div className="flex flex-wrap gap-6 text-sm">
            <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-[#3b82f6] hover:text-[#0a0a0a]" data-testid="terms-email">
              <Mail className="w-4 h-4" /> {c.email}
            </a>
            <a href={`tel:${c.phone.replace(/\s+/g, '')}`} className="flex items-center gap-2 text-[#3b82f6] hover:text-[#0a0a0a]" dir="ltr" data-testid="terms-phone">
              <Phone className="w-4 h-4" /> {c.phone}
            </a>
            <div className="flex items-center gap-2 text-[#111827]/60">
              {c.address}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
